import type { ChartSeries } from "../aidwops.types";
import tooltipsData from "./AidwopsTooltips.json";

// A fixed, small palette cycled by index -- real AIOps posture widgets
// (Datadog, Splunk ITSI) use a categorical palette for composition charts
// like this, not a semantic red/green mapping, since the categories here
// (rule actions, OS names, compliance states) aren't uniformly "good/bad".
const PALETTE = ["var(--success)", "var(--eon-red)", "var(--teal)", "#d99a00", "var(--faint)", "var(--ink-soft)"];

const postureTooltips: Record<string, string> = tooltipsData.posture || {};

function total(series: ChartSeries) {
  return series.data.reduce((sum, p) => sum + p.value, 0);
}

function DonutChart({ series }: { series: ChartSeries }) {
  const sum = total(series);
  const r = 34;
  const circumference = 2 * Math.PI * r;
  let offset = 0;

  return (
    <div className="chart-body">
      <svg className="donut-chart" width="92" height="92" viewBox="0 0 92 92" role="img" aria-label={series.title}>
        <circle cx="46" cy="46" r={r} fill="none" stroke="var(--border-soft)" strokeWidth="14" />
        {sum > 0 &&
          series.data.map((point, i) => {
            const frac = point.value / sum;
            const dash = frac * circumference;
            const circle = (
              <circle
                key={point.label}
                cx="46"
                cy="46"
                r={r}
                fill="none"
                stroke={PALETTE[i % PALETTE.length]}
                strokeWidth="14"
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={-offset}
                transform="rotate(-90 46 46)"
              />
            );
            offset += dash;
            return circle;
          })}
      </svg>
      <div className="chart-legend-list">
        {series.data.map((point, i) => (
          <div key={point.label} className="chart-legend-row chart-legend-row--donut">
            <span className="chart-legend-swatch" style={{ background: PALETTE[i % PALETTE.length] }} />
            <span className="chart-legend-label">{point.label}</span>
            <span className="chart-legend-value">{point.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BarChart({ series }: { series: ChartSeries }) {
  const max = Math.max(...series.data.map((p) => p.value), 1);
  return (
    <div className="chart-legend-list">
      {series.data.map((point, i) => (
        <div key={point.label} className="chart-legend-row chart-legend-row--bar" title={point.label}>
          <span className="chart-legend-label">{point.label}</span>
          <div className="bar-track">
            <div
              className="bar-fill"
              style={{ width: `${(point.value / max) * 100}%`, background: PALETTE[i % PALETTE.length] }}
            />
          </div>
          <span className="chart-legend-value">{point.value}</span>
        </div>
      ))}
    </div>
  );
}

export function PostureCharts({ charts, loading }: { charts: ChartSeries[]; loading?: boolean }) {
  if (charts.length === 0 && !loading) return null;
  return (
    <section>
      <div className="section-head">
        <span className="section-title">Posture</span>
      </div>
      <div className="charts-row">
        {loading &&
          charts.length === 0 &&
          Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="skeleton-card">
              <div className="skeleton skeleton-line" style={{ width: "40%", marginBottom: 12 }} />
              <div style={{ display: "flex", gap: 18 }}>
                <div className="skeleton" style={{ width: 84, height: 84, borderRadius: "50%" }} />
                <div style={{ flex: 1 }}>
                  <div className="skeleton skeleton-line" />
                  <div className="skeleton skeleton-line" />
                  <div className="skeleton skeleton-line" />
                </div>
              </div>
            </div>
          ))}
        {charts.map((series) => {
          const tooltip = postureTooltips[series.id];
          return (
            <div key={series.id} className="chart-card" title={tooltip}>
              <div style={{ marginBottom: 10 }}>
                <div className="card-title">{series.title}</div>
                {series.detail && <div className="card-title-sub">{series.detail}</div>}
              </div>
              {series.type === "donut" ? <DonutChart series={series} /> : <BarChart series={series} />}
            </div>
          );
        })}
      </div>
    </section>
  );
}
